import { getSafeReferrerUrl, isUtmParam } from '@/utils/url';

const STORAGE_KEY_LANDING_URL = 'attribution_landing_url';
const STORAGE_KEY_REFERRER = 'attribution_referrer';

/**
 * Returns stored landing attribution data for GA event payloads.
 * Each field is a separate GA4 custom dimension (100-char limit each).
 */
export interface LandingAttributionData {
  landingPath: string;
  landingSource: string;
  landingMedium: string;
  landingCampaign: string;
  landingContent: string;
  landingReferrer: string;
  landingFrom: string;
}

/**
 * Captures the landing URL and document.referrer into sessionStorage.
 * Should be called once on initial page load. Skips if already captured this session.
 *
 * ⚠️ 프리렌더된 gene 정적 페이지는 React 가 없어 이 함수 대신
 * scripts/pack-gene-pages.ts 의 TRACKING_SNIPPET 이 같은 동작을 바닐라로 재현한다.
 * storage key·정제 규칙을 바꾸면 그쪽도 함께 바꿔야 한다.
 */
export const captureLandingAttribution = (): void => {
  try {
    if (sessionStorage.getItem(STORAGE_KEY_LANDING_URL)) return;

    sessionStorage.setItem(STORAGE_KEY_LANDING_URL, window.location.href);
    sessionStorage.setItem(
      STORAGE_KEY_REFERRER,
      getSafeReferrerUrl(document.referrer, { includeUtm: false }),
    );
  } catch {
    // sessionStorage unavailable (private browsing, storage full, etc.)
  }
};

/**
 * Builds a composite reference URL for form submissions:
 * current page href + UTM params from landing page + first_referrer query param.
 */
export const buildFormUrlWithLandingUtm = (): string => {
  try {
    const currentUrl = new URL(window.location.href);

    const storedLandingUrl = sessionStorage.getItem(STORAGE_KEY_LANDING_URL);
    if (storedLandingUrl) {
      const parsed = new URL(storedLandingUrl);
      if (parsed.origin !== window.location.origin)
        return currentUrl.toString();

      parsed.searchParams.forEach((value, key) => {
        if (isUtmParam(key) && !currentUrl.searchParams.has(key)) {
          currentUrl.searchParams.set(key, value);
        }
      });
    }

    const storedReferrer = sessionStorage.getItem(STORAGE_KEY_REFERRER);
    if (storedReferrer) {
      currentUrl.searchParams.set('first_referrer', storedReferrer);
    }

    return currentUrl.toString();
  } catch {
    // sessionStorage or URL parsing unavailable
    return window.location.href;
  }
};

export const generateSubscribeDataString = (email: string): string => {
  const params = new URLSearchParams();
  params.append('email', email);
  params.append('Referrer', buildFormUrlWithLandingUtm());
  return params.toString();
};

/**
 * Returns stored landing attribution data for GA event payloads.
 * Each field is a separate GA4 custom dimension (100-char limit each).
 */
export const getLandingAttributionData = (): LandingAttributionData => {
  try {
    const storedLandingUrl = sessionStorage.getItem(STORAGE_KEY_LANDING_URL);
    const parsed = storedLandingUrl ? new URL(storedLandingUrl) : null;

    return {
      landingPath: parsed?.pathname ?? '',
      landingSource: parsed?.searchParams.get('utm_source') ?? '',
      landingMedium: parsed?.searchParams.get('utm_medium') ?? '',
      landingCampaign: parsed?.searchParams.get('utm_campaign') ?? '',
      landingContent: parsed?.searchParams.get('utm_content') ?? '',
      landingReferrer: [
        parsed?.searchParams.get('ref')
          ? `ref=${parsed.searchParams.get('ref')}`
          : '',
        sessionStorage.getItem(STORAGE_KEY_REFERRER) ?? '',
      ]
        .filter(Boolean)
        .join(' | '),
      landingFrom: parsed?.searchParams.get('from') ?? '',
    };
  } catch {
    // sessionStorage unavailable
    return {
      landingPath: '',
      landingSource: '',
      landingMedium: '',
      landingCampaign: '',
      landingContent: '',
      landingReferrer: '',
      landingFrom: '',
    };
  }
};
